<template>
  <Dialog
    :visible="visible"
    @update:visible="() => $emit('update:visible', false)"
    modal
    :closable="false"
    class="max-w-5xl">
    <template #header>
      <div class="flex w-full justify-end space-x-4">
        <Button severity="secondary" label="Cancel" outlined @click="() => $emit('update:visible', false)" />
        <Button v-if="isEditing" severity="danger" label="Remove" outlined @click="handleRemove" />
        <Button :label="`${isEditing ? 'Update' : 'Add'} Conditional Content`" @click="save()" />
      </div>
    </template>

    <div class="space-y-6">
      <div v-if="!isEditing" class="conditional-mode-toggle">
        <SelectButton
          v-model="mode"
          :options="modeOptions"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          class="w-full"
        />
      </div>

      <template v-if="editorMode === 'quick'">
        <!-- Question Selection -->
        <div v-if="!selectedSegmentGroupId">
          <FormGroup label="Show different content based on a question:">
            <template #description v-if="segmentationErrors">
              <span class="text-sm text-red-800">
                {{ segmentationErrors }}
              </span>
            </template>
          </FormGroup>

          <div class="mt-4">
            <GridList
              class="mt-8"
              name="questions-for-conditional"
              :items="filteredItems"
              @filter="filterQuery = $event"
              :showList="false"
              :bigFilter="true"
              :columns="[]"
              :columnCount="2"
              @row-select="selectQuestion($event.data.id)">
              <template #grid-item="{ item }">
                <div
                  @click="selectQuestion(item.id)"
                  class="bg-surface-50/30 ring-surface-200/70 hover:ring-surface-900/50 dark:bg-surface-950/20 dark:ring-surface-200/20 dark:hover:ring-surface-200/50 cursor-pointer rounded p-6 shadow ring ring-1 transition-all duration-100">
                  <div class="space-y-2">
                    <div class="text-surface-800 text-sm font-semibold" v-html="item.name"></div>
                    <div class="text-lg" v-html="item.question"></div>
                    <div class="text-sm opacity-70" v-html="item.options.join(' / ')"></div>
                  </div>
                </div>
              </template>
            </GridList>
          </div>
        </div>

        <!-- Segment Content Configuration -->
        <div v-else>
          <div class="mb-4 flex items-center justify-between">
            <h3 class="text-lg font-semibold">Configure content for each segment</h3>
            <Button
              @click="changeQuestion"
              icon="pi pi-arrow-left"
              label="Change Question"
              outlined
              severity="secondary"
              size="small" />
          </div>

          <FormGroup v-if="selectedSegmentGroup" label="Default content (shown if no conditions match):">
            <RichTextEditor v-model="elseContent" class="w-full" />
          </FormGroup>

          <FormGroup v-if="selectedSegmentGroup" class="mt-6" label="Condition type:">
            <div class="flex items-center gap-4">
              <div class="flex items-center">
                <RadioButton v-model="conditionOperator" inputId="operator-equals" value="==" name="operator" />
                <label for="operator-equals" class="ml-2 cursor-pointer">Equals (exact match)</label>
              </div>
              <div class="flex items-center">
                <RadioButton v-model="conditionOperator" inputId="operator-contains" value="contains" name="operator" />
                <label for="operator-contains" class="ml-2 cursor-pointer">Contains (partial match)</label>
              </div>
            </div>
          </FormGroup>

          <FormGroup v-if="selectedSegmentGroup" :label="`Conditional content:`" class="mt-8">
            <div class="ring-primary-600/10 ring-2">
              <div
                v-for="(segment, idx) in selectedSegmentGroup.segments"
                :key="segment.id"
                class="space-y-4 p-4"
                :class="{ 'bg-primary-600/10': idx % 2 }">
                <div class="flex items-center">
                  <div>
                    <div class="font-bold">
                      {{ stripHtml(segmentName(segment)) }}
                    </div>
                    <div class="hidden text-xs text-gray-500">
                      {{ getSegmentSyncValue(segment) }}
                    </div>
                  </div>
                </div>
                <div class="flex items-center pr-6">
                  <RichTextEditor v-model="conditions[segment.id]" class="w-full" />
                </div>
              </div>
            </div>
          </FormGroup>
        </div>
      </template>

      <template v-else>
        <FormGroup label="Default content (shown if no branches match):">
          <RichTextEditor v-model="elseContent" class="w-full" />
        </FormGroup>

        <div class="space-y-4">
          <Card v-for="(branch, branchIndex) in advancedBranches" :key="branch.id" class="group">
            <template #content>
              <div class="rounded-md py-3">
                <div class="mb-6 flex items-center justify-between gap-x-3 dark:text-white/90">
                  <div class="flex flex-wrap items-center gap-3">
                    <h3 class="font-semibold">Branch {{ branchIndex + 1 }}</h3>
                    <Select
                      v-model="branch.match"
                      :options="branchMatchOptions"
                      option-label="label"
                      option-value="value"
                      class="min-w-[18rem]"
                    />
                  </div>
                  <Button
                    v-if="advancedBranches.length > 1"
                    size="small"
                    outlined
                    icon="pi pi-trash"
                    @click="removeBranch(branchIndex)"
                  />
                </div>

                <div>
                  <div
                    v-for="(rule, ruleIndex) in branch.rules"
                    :key="rule.id"
                    class="flex items-start space-x-2"
                    :class="{ 'mt-4': ruleIndex > 0 }">
                    <div class="flex-grow">
                      <div class="flex flex-wrap items-stretch gap-x-2 gap-y-3">
                        <Select
                          :model-value="rule.fieldId || null"
                          :options="groupedFieldCatalog"
                          option-label="primaryLabel"
                          option-value="id"
                          optionGroupLabel="label"
                          optionGroupChildren="options"
                          placeholder="Choose a field..."
                          filter
                          class="min-w-[18rem] flex-1"
                          :overlayStyle="{ maxWidth: '28rem' }"
                          @update:model-value="selectField(branchIndex, ruleIndex, $event ?? '')">
                          <template #option="{ option }">
                            <div class="w-[24rem] max-w-full min-w-0">
                              <div class="truncate text-sm font-medium">{{ option.primaryLabel }}</div>
                              <div v-if="option.secondaryLabel" class="truncate text-xs opacity-60">
                                {{ option.secondaryLabel }}
                              </div>
                            </div>
                          </template>
                        </Select>

                        <Select
                          v-model="rule.operator"
                          :options="operatorOptionsForRule(rule)"
                          option-label="label"
                          option-value="value"
                          class="min-w-[11rem]"
                        />

                        <Select
                          v-if="valuesForRule(rule).length"
                          v-model="rule.value"
                          :options="valuesForRule(rule)"
                          option-label="label"
                          option-value="value"
                          placeholder="Choose a value..."
                          class="min-w-[16rem] flex-1"
                        />
                        <InputText
                          v-else
                          v-model="rule.value"
                          class="min-w-[16rem] flex-1"
                          placeholder="Value"
                        />
                      </div>
                    </div>
                    <div class="flex-shrink-0">
                      <XMarkIcon
                        v-if="branch.rules.length > 1"
                        @click="removeRule(branchIndex, ruleIndex)"
                        class="ml-2 mt-3.5 w-4 cursor-pointer"
                      />
                      <div v-else class="ml-2 mt-3.5 w-4" />
                    </div>
                  </div>
                </div>

                <div class="mt-4 flex space-x-4">
                  <Button
                    @click="addRule(branchIndex)"
                    label="Add conditions"
                    size="small"
                    severity="subtle"
                    icon="pi pi-plus"
                  />
                </div>

                <FormGroup class="mt-6" label="Content shown when this branch matches:">
                  <RichTextEditor v-model="branch.content" class="w-full" />
                </FormGroup>
              </div>
            </template>
          </Card>

          <Button @click="addBranch()" label="Add branch" size="small" severity="subtle" icon="pi pi-plus" />
        </div>
      </template>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { stripHtml } from '@/services/string'
import {
  segmentName,
  segmentGroupSyncSetupCompletion,
  isAQuestion,
  renderText,
  autoSyncLogicDefinitionForSegment
} from '@/modules/segment/services/segmentUtilities'
import { customFieldKey } from '@/modules/integration/services/integrationUtilities'
import { uuid } from '@/services/uuid'
import {
  buildEmailPersonalizationFieldCatalog,
  type EmailPersonalizationField
} from '@/modules/personalization/services/emailPersonalizationFieldCatalog'
import {
  detectConditionValueType,
  normalizeConditionRuleValue,
  type ConditionOperator,
  type ConditionalBranch,
  type ConditionRule,
  type ConditionValueType
} from './liquidConditionals'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'update:visible', value: boolean): void
  (e: 'remove', data: any): void
}>()

const props = defineProps<{
  visible: boolean
  fallbackText?: string
  conditionalId?: string
  existingBranches?: Array<ConditionalBranch>
  existingConditions?: Array<any>
  existingElseContent?: string
}>()

type AdvancedRule = ConditionRule & { id: string; fieldId: string; valueType: ConditionValueType }
type AdvancedBranch = { id: string; match: 'all' | 'any'; rules: AdvancedRule[]; content: string }
type GroupedFieldOption = EmailPersonalizationField & { primaryLabel: string; secondaryLabel?: string }

const segmentGroupStore = useSegmentGroupStore()
const questionStore = useQuestionStore()
const { segmentGroups } = storeToRefs(segmentGroupStore)
const filterQuery = ref('')
const segmentationErrors = ref<string | null>(null)
const confirm = inject('confirm') as any

const integrationStore = useIntegrationStore()
const { integratedPartner } = storeToRefs(integrationStore)

const fieldCatalog = ref<EmailPersonalizationField[]>([])
const groupedFieldCatalog = computed(() => {
  const groups = [
    { label: 'RightMessage questions', sourceTypes: ['segment'] as const },
    { label: 'Native fields', sourceTypes: ['native_field'] as const },
    { label: 'Custom fields', sourceTypes: ['custom_field'] as const },
    { label: 'Tags', sourceTypes: ['tag'] as const }
  ]

  const optionForField = (field: EmailPersonalizationField): GroupedFieldOption => {
    if (field.sourceType !== 'segment') {
      return { ...field, primaryLabel: field.label }
    }

    const segmentGroupId = field.id.startsWith('segment:') ? field.id.slice('segment:'.length) : null
    const segmentGroup = segmentGroupId ? segmentGroupStore.getById(segmentGroupId) : null
    const primaryLabel = stripHtml(segmentGroup?.name || field.label)
    const secondaryLabel = field.label !== primaryLabel ? field.label : undefined

    return { ...field, primaryLabel, secondaryLabel }
  }

  return groups
    .map((group) => ({
      label: group.label,
      options: fieldCatalog.value
        .filter((field) => group.sourceTypes.includes(field.sourceType))
        .map(optionForField)
    }))
    .filter((group) => group.options.length > 0)
})
const modeOptions: Array<{ label: string; value: 'quick' | 'advanced' }> = [
  { label: 'Question quick mode', value: 'quick' },
  { label: 'Advanced rules', value: 'advanced' }
]
const branchMatchOptions: Array<{ label: string; value: AdvancedBranch['match'] }> = [
  { label: 'All rules must match (AND)', value: 'all' },
  { label: 'Any rule may match (OR)', value: 'any' }
]
const mode = ref<'quick' | 'advanced'>('quick')

const objectName = computed(() => {
  if (integratedPartner.value && Object.prototype.hasOwnProperty.call(integratedPartner.value, 'liquidVariable')) {
    return integratedPartner.value.liquidVariable || null
  }
  return 'subscriber'
})

const availableSegmentGroups = computed(() => {
  return segmentGroups.value.filter((sg) => {
    return (
      isAQuestion(sg) &&
      (segmentGroupSyncSetupCompletion(sg) === 'some_values' || segmentGroupSyncSetupCompletion(sg) === 'all_values')
    )
  })
})

const segmentGroupsForSearch = computed(() => {
  return availableSegmentGroups.value.map((sg) => {
    const question = questionStore.getBySegmentGroupId(sg.id)
    return {
      id: sg.id,
      question: stripHtml(renderText(question?.text || '', 1000)),
      name: sg.name,
      options: sg.segments.map((s: any) => stripHtml(segmentName(s)))
    }
  })
})

const highlightMatch = (text: string, query: string) => {
  if (!query) return text
  const regex = new RegExp(`(${query})`, 'gi')
  return text.replace(regex, '<span class="found">$1</span>')
}

const filteredItems = computed(() => {
  if (!filterQuery.value) return segmentGroupsForSearch.value

  const query = filterQuery.value.toLowerCase()
  return segmentGroupsForSearch.value
    .filter((sg) => {
      const nameMatch = sg.name.toLowerCase().includes(query)
      const questionMatch = sg.question.toLowerCase().includes(query)
      const optionsMatch = sg.options.some((s: string) => s.toLowerCase().includes(query))

      return nameMatch || questionMatch || optionsMatch
    })
    .map((sg) => ({
      ...sg,
      name: highlightMatch(sg.name, query),
      question: highlightMatch(sg.question, query),
      options: sg.options.map((s: string) => highlightMatch(s, query))
    }))
})

const selectQuestion = (segmentGroupId: string) => {
  selectedSegmentGroupId.value = segmentGroupId
  conditions.value = {}
}

const changeQuestion = () => {
  const hasContent = Object.values(conditions.value).some((content) => content && content.trim() !== '')

  if (hasContent) {
    confirm.require({
      message:
        'You have entered content for one or more segments. Changing the question will lose all of this content.',
      header: 'Change Question?',
      acceptProps: { label: 'Change Question', severity: 'warning' },
      rejectProps: { label: 'Cancel', outlined: true },
      accept: async () => {
        selectedSegmentGroupId.value = null
        conditions.value = {}
        filterQuery.value = ''
        await focusSearchInput()
      }
    })
  } else {
    selectedSegmentGroupId.value = null
    conditions.value = {}
    filterQuery.value = ''
    focusSearchInput()
  }
}

const selectedSegmentGroupId = ref<string | null>(null)
const elseContent = ref(props.fallbackText || '')
const conditions = ref<Record<string, string>>({})
const conditionalId = ref(props.conditionalId || null)
const conditionOperator = ref<ConditionOperator>('==')
const advancedBranches = ref<AdvancedBranch[]>([])

const isEditing = computed(() => {
  return !!(props.conditionalId || conditionalId.value)
})
const editorMode = computed<'quick' | 'advanced'>(() => {
  return isEditing.value ? 'advanced' : mode.value
})

const focusSearchInput = async () => {
  await nextTick()
  const searchInput = document.querySelector('input[size="large"][placeholder="Search"]') as HTMLInputElement
  if (searchInput) searchInput.focus()
}

const selectedSegmentGroup = computed(() => {
  if (!selectedSegmentGroupId.value) return null
  return segmentGroupStore.getById(selectedSegmentGroupId.value)
})

const getSegmentSyncValue = (segment: any) => {
  if (!selectedSegmentGroup.value || !segment) return ''
  return autoSyncLogicDefinitionForSegment(segment)?.value || ''
}

const loadFieldCatalog = async () => {
  fieldCatalog.value = await buildEmailPersonalizationFieldCatalog({
    integratedPartner: integratedPartner.value,
    segmentGroups: segmentGroups.value,
    questionForSegmentGroupId: (segmentGroupId: string) => questionStore.getBySegmentGroupId(segmentGroupId)
  })
}

const resetAdvancedBranches = () => {
  advancedBranches.value = [newAdvancedBranch()]
}

const newAdvancedRule = (): AdvancedRule => ({
  id: uuid(),
  fieldId: '',
  sourceType: 'unknown',
  label: '',
  token: '',
  operator: '==',
  value: '',
  valueType: 'string'
})

const newAdvancedBranch = (content = ''): AdvancedBranch => ({
  id: uuid(),
  match: 'all',
  rules: [newAdvancedRule()],
  content
})

const addBranch = () => {
  advancedBranches.value.push(newAdvancedBranch())
}

const removeBranch = (branchIndex: number) => {
  advancedBranches.value.splice(branchIndex, 1)
}

const addRule = (branchIndex: number) => {
  advancedBranches.value[branchIndex].rules.push(newAdvancedRule())
}

const removeRule = (branchIndex: number, ruleIndex: number) => {
  advancedBranches.value[branchIndex].rules.splice(ruleIndex, 1)
}


const selectField = (branchIndex: number, ruleIndex: number, fieldId: string) => {
  const field = fieldCatalog.value.find((item) => item.id === fieldId)
  const rule = advancedBranches.value[branchIndex].rules[ruleIndex]
  rule.fieldId = fieldId
  if (!field) {
    rule.sourceType = 'unknown'
    rule.label = ''
    rule.token = ''
    rule.operator = '=='
    rule.value = ''
    rule.valueType = 'string'
    return
  }

  rule.sourceType = field.sourceType
  rule.label = field.label
  rule.token = field.token
  rule.operator = field.defaultOperator
  rule.valueType = field.valueType
  rule.value = field.values?.[0]?.value || ''
}

const fieldForRule = (rule: AdvancedRule) => {
  return fieldCatalog.value.find((field) => field.id === rule.fieldId)
}

const operatorsForRule = (rule: AdvancedRule) => {
  return fieldForRule(rule)?.operators || ['==', '!=', 'contains', '>', '<', '>=', '<=']
}

const operatorOptionsForRule = (rule: AdvancedRule) => {
  return operatorsForRule(rule).map((operator) => ({
    label: operatorLabel(operator),
    value: operator
  }))
}

const valuesForRule = (rule: AdvancedRule) => {
  return fieldForRule(rule)?.values || []
}

const operatorLabel = (operator: ConditionOperator) => {
  if (operator === '==') return 'equals'
  if (operator === '!=') return 'does not equal'
  if (operator === 'contains') return 'contains'
  return operator
}

const advancedRuleFromRule = (rule: ConditionRule): AdvancedRule => {
  const matchingField =
    fieldCatalog.value.find((field) => field.token === rule.token && field.sourceType === rule.sourceType) ||
    fieldCatalog.value.find((field) => field.token === rule.token)

  const sourceType =
    rule.sourceType && rule.sourceType !== 'unknown' ? rule.sourceType : matchingField?.sourceType || 'unknown'
  const label =
    rule.label && rule.label !== rule.token ? rule.label : matchingField?.label || rule.label || rule.token

  return {
    id: uuid(),
    fieldId: matchingField?.id || '',
    sourceType,
    label,
    token: rule.token,
    operator: rule.operator || matchingField?.defaultOperator || '==',
    value: rule.value ?? '',
    valueType: matchingField?.valueType || detectConditionValueType(rule.value ?? '')
  }
}

const loadExistingAdvancedBranches = () => {
  const branches = props.existingBranches?.length
    ? props.existingBranches
    : (props.existingConditions || []).map((condition) => ({
        match: 'all' as const,
        rules: [
          {
            sourceType: 'segment' as const,
            label: condition.property,
            token: condition.property,
            operator: condition.operator || '==',
            value: condition.value
          }
        ],
        content: condition.content
      }))

  advancedBranches.value = branches.length
    ? branches.map((branch) => ({
        id: uuid(),
        match: branch.match || 'all',
        rules: branch.rules.map(advancedRuleFromRule),
        content: branch.content || ''
      }))
    : [newAdvancedBranch()]
}

const tryLoadQuickMode = () => {
  if (!props.existingConditions?.length) return false
  const firstCondition = props.existingConditions[0]
  if (!firstCondition?.property) return false

  const propertyParts = firstCondition.property.split('.')
  const customFieldIdOrKey = propertyParts.length > 1 ? propertyParts[1] : propertyParts[0]

  const foundSegmentGroup = segmentGroups.value.find((sg) => {
    return (
      sg.syncToCustomField === customFieldIdOrKey &&
      props.existingConditions?.every((condition) => {
        return sg.segments.some((segment: any) => autoSyncLogicDefinitionForSegment(segment)?.value === condition.value)
      })
    )
  })

  if (!foundSegmentGroup) return false

  selectedSegmentGroupId.value = foundSegmentGroup.id
  conditions.value = {}
  conditionOperator.value = props.existingConditions[0]?.operator || '=='
  props.existingConditions.forEach((condition) => {
    const segment = foundSegmentGroup.segments.find(
      (s: any) => autoSyncLogicDefinitionForSegment(s)?.value === condition.value
    )
    if (segment) conditions.value[segment.id] = condition.content
  })
  return true
}

watch(
  () => props.visible,
  async (newValue) => {
    if (!newValue) return

    conditionalId.value = props.conditionalId || null
    elseContent.value = props.existingElseContent || props.fallbackText || ''
    await loadFieldCatalog()
    if (isEditing.value) {
      mode.value = 'advanced'
      loadExistingAdvancedBranches()
      return
    }

    if (props.existingBranches?.some((branch) => branch.rules.length > 1 || branch.match === 'any')) {
      mode.value = 'advanced'
      loadExistingAdvancedBranches()
      return
    }

    if (props.existingConditions && props.existingConditions.length > 0) {
      const loadedQuickMode = tryLoadQuickMode()
      if (loadedQuickMode) {
        mode.value = 'quick'
        loadExistingAdvancedBranches()
      } else {
        mode.value = 'advanced'
        loadExistingAdvancedBranches()
      }
      return
    }

    mode.value = 'quick'
    selectedSegmentGroupId.value = null
    conditions.value = {}
    conditionOperator.value = '=='
    resetAdvancedBranches()
    await focusSearchInput()
  }
)

watch(
  () => selectedSegmentGroupId.value,
  () => {
    if (!isEditing.value) conditions.value = {}
  }
)

watch(
  () => integratedPartner.value,
  () => {
    if (props.visible) loadFieldCatalog()
  }
)

const tokenForCustomField = async (fieldId: string) => {
  const fieldKey = await customFieldKey(fieldId)
  return objectName.value ? `${objectName.value}.${fieldKey}` : fieldKey
}

const quickBranches = async (): Promise<ConditionalBranch[]> => {
  if (!selectedSegmentGroup.value) return []

  const token = await tokenForCustomField(selectedSegmentGroup.value.syncToCustomField)
  return selectedSegmentGroup.value.segments.flatMap((segment: any) => {
    const content = conditions.value[segment.id]
    const syncValue = getSegmentSyncValue(segment)
    if (!content || !syncValue) return []

    return [
      {
        match: 'all' as const,
        rules: [
          {
            sourceType: 'segment' as const,
            label: stripHtml(segmentName(segment)),
            token,
            operator: conditionOperator.value,
            value: syncValue
          }
        ],
        content
      }
    ]
  })
}

const cleanAdvancedBranches = (): ConditionalBranch[] => {
  return advancedBranches.value.flatMap((branch) => {
    const rules = branch.rules
      .filter((rule) => rule.token && rule.value !== '' && rule.value !== null && rule.value !== undefined)
      .map((rule) => ({
        sourceType: rule.sourceType,
        label: rule.label || rule.token,
        token: rule.token,
        operator: rule.operator,
        value: normalizeConditionRuleValue(rule.value, rule.valueType)
      }))

    if (!rules.length || !branch.content) return []
    return [{ match: branch.match, rules, content: branch.content }]
  })
}

const save = async () => {
  const branches = editorMode.value === 'quick' ? await quickBranches() : cleanAdvancedBranches()
  if (!branches.length) {
    alert(
      editorMode.value === 'quick' ? 'Please add content for at least one segment' : 'Please add at least one complete branch'
    )
    return
  }

  emit('save', {
    conditionalId: conditionalId.value || uuid(),
    branches,
    conditions: branches.map((branch) => ({
      property: branch.rules[0]?.token,
      operator: branch.rules[0]?.operator,
      value: branch.rules[0]?.value,
      content: branch.content
    })),
    elseContent: elseContent.value
  })

  conditionalId.value = null
  selectedSegmentGroupId.value = null
  elseContent.value = ''
  conditions.value = {}
  conditionOperator.value = '=='
  resetAdvancedBranches()
}

const handleRemove = () => {
  emit('remove', {
    conditionalId: conditionalId.value,
    elseContent: elseContent.value
  })
  emit('update:visible', false)
}
</script>

<style scoped>
.found {
  background-color: rgba(255, 255, 0, 0.3);
  font-weight: bold;
}

.conditional-mode-toggle :deep(.p-selectbutton) {
  display: flex;
  width: 100%;
}

.conditional-mode-toggle :deep(.p-selectbutton > *) {
  flex: 1 1 0;
  min-width: 0;
}
</style>